import {
  mdiCarSide,
  mdiChartAreaspline,
  mdiFileDocumentOutline,
  mdiHelpCircleOutline,
  mdiKeyboard,
  mdiKeyboardOutline,
  mdiPaletteOutline,
  mdiSpeedometer,
  mdiTrophyOutline,
} from "@mdi/js";
import { defaultLocale, landingLocales, type LocaleId } from "@typeblind/intl";
import { defineMessage, type MessageDescriptor } from "react-intl";
import { type AnonymousUser, type AnyUser, type NamedUser } from "./types.ts";

export type Meta = {
  readonly name?: string;
  readonly property?: string;
  readonly content: string | MessageDescriptor;
};

export type PageInfo = {
  readonly path: string;
  readonly title: MessageDescriptor;
  readonly link: {
    readonly label: MessageDescriptor;
    readonly title?: MessageDescriptor;
    readonly icon?: string;
    /**
     * True for pages not served by this SPA (e.g. Blog, reverse-proxied to
     * a separate app at the same domain). MenuItemLink renders these as a
     * plain <a> that does a real browser navigation, instead of a
     * client-side <NavLink> that the app router can't resolve.
     */
    readonly external?: boolean;
  };
  readonly meta: Meta[];
};

export namespace Pages {
  const meta: Meta[] = [
    { property: "og:type", content: "website" },
    { property: "og:site_name", content: "Typeblind — Typing lessons" },
    { property: "og:title", content: "Typeblind — Typing lessons" },
    {
      property: "og:description",
      content:
        "Learn to type faster with Typeblind — adaptive lessons, real-time feedback, no looking down.",
    },
    // No `www`: that host answers 301, and a share card whose image sits
    // behind a redirect comes out blank on the networks that do not follow
    // one. `og:url` is not here at all - it has to differ per page, so the
    // server emits it from the canonical URL (see CanonicalLink).
    { property: "og:image", content: "https://typeblind.com/cover.png" },
    { name: "twitter:card", content: "summary" },
    { name: "twitter:site", content: "@typeblindapp" },
    { name: "twitter:creator", content: "@typeblindapp" },
  ];

  export const account = {
    path: "/account",
    title: defineMessage({
      id: "t_Account",
      defaultMessage: "Account",
    }),
    link: {
      label: defineMessage({
        id: "t_Account",
        defaultMessage: "Account",
      }),
    },
    meta: [{ name: "robots", content: "noindex" }],
  } satisfies PageInfo;

  export const landing = {
    path: "/",
    title: defineMessage({
      id: "t_Landing_Title",
      defaultMessage: "Typeblind — Type without looking down",
    }),
    link: {
      label: defineMessage({
        id: "t_Home",
        defaultMessage: "Home",
      }),
    },
    meta,
  } satisfies PageInfo;

  export const practice = {
    path: "/practice",
    title: defineMessage({
      id: "page.practice.title",
      defaultMessage: "Touch Typing Practice With Adaptive Lessons | Typeblind",
    }),
    link: {
      label: defineMessage({
        id: "t_Practice",
        defaultMessage: "Practice",
      }),
      title: defineMessage({
        id: "page.practice.description",
        defaultMessage:
          "Free touch typing lessons that adapt to you: every lesson is built from the keys you miss most, so five minutes a day turns into real speed and accuracy.",
      }),
      icon: mdiKeyboard,
    },
    meta,
  } satisfies PageInfo;

  export const profile = {
    path: "/profile",
    title: defineMessage({
      id: "t_Profile",
      defaultMessage: "Profile",
    }),
    link: {
      label: defineMessage({
        id: "t_Profile",
        defaultMessage: "Profile",
      }),
      title: defineMessage({
        id: "page.profile.description",
        defaultMessage:
          "The detailed statistics regarding your learning progress.",
      }),
      icon: mdiChartAreaspline,
    },
    meta: [{ name: "robots", content: "noindex" }],
  } satisfies PageInfo;

  export const blog = {
    path: "/blog",
    title: defineMessage({
      id: "t_Blog",
      defaultMessage: "Blog",
    }),
    link: {
      label: defineMessage({
        id: "t_Blog",
        defaultMessage: "Blog",
      }),
      title: defineMessage({
        id: "page.blog.description",
        defaultMessage:
          "Articles about touch typing, keyboard layouts, and learning to type faster.",
      }),
      icon: mdiFileDocumentOutline,
      // Reverse-proxied to a separate app (Stateinik) at /blog — not part
      // of this SPA's router.
      external: true,
    },
    meta,
  } satisfies PageInfo;

  export const help = {
    path: "/help",
    title: defineMessage({
      id: "page.help.title",
      defaultMessage: "How Typeblind Works: Lessons, Metrics and Settings",
    }),
    link: {
      label: defineMessage({
        id: "t_Help",
        defaultMessage: "Help",
      }),
      title: defineMessage({
        id: "page.help.description",
        defaultMessage:
          "How Typeblind’s adaptive lessons pick the keys you practise next, what the speed and accuracy numbers mean, and how to set up your keyboard layout.",
      }),
      icon: mdiHelpCircleOutline,
    },
    meta,
  } satisfies PageInfo;

  export const highScores = {
    path: "/high-scores",
    title: defineMessage({
      id: "page.highScores.title",
      defaultMessage: "Typing Speed Leaderboard: Fastest Typists | Typeblind",
    }),
    link: {
      label: defineMessage({
        id: "t_High_Scores",
        defaultMessage: "High Scores",
      }),
      title: defineMessage({
        id: "page.highScores.description",
        defaultMessage:
          "The fastest typing results on Typeblind by keyboard layout and text type. See the top words-per-minute scores and how your own speed compares.",
      }),
      icon: mdiTrophyOutline,
    },
    meta,
  } satisfies PageInfo;

  export const multiplayer = {
    path: "/multiplayer",
    title: defineMessage({
      id: "page.multiplayer.title",
      defaultMessage:
        "Multiplayer Typing Race: Race Other Typists Live | Typeblind",
    }),
    link: {
      label: defineMessage({
        id: "t_Multiplayer",
        defaultMessage: "Multiplayer",
      }),
      title: defineMessage({
        id: "page.multiplayer.description",
        defaultMessage:
          "Race other typists in real time in a free online typing game. Get your own driver number, pull ahead with clean keystrokes and see where you finish.",
      }),
      icon: mdiCarSide,
    },
    meta,
  } satisfies PageInfo;

  export const typingTest = {
    path: "/typing-test",
    title: defineMessage({
      id: "page.typingTest.title",
      defaultMessage:
        "Typing Speed Test: Check Your WPM and Accuracy | Typeblind",
    }),
    link: {
      label: defineMessage({
        id: "t_Typing_Test",
        defaultMessage: "Typing Test",
      }),
      title: defineMessage({
        id: "page.typingTest.description",
        defaultMessage:
          "Take a free typing speed test in your browser, with no sign-up needed. See your words per minute and accuracy the moment you finish, and try again any time.",
      }),
      icon: mdiSpeedometer,
    },
    meta,
  } satisfies PageInfo;

  export const layouts = {
    path: "/layouts",
    title: defineMessage({
      id: "page.layouts.title",
      defaultMessage: "Keyboard Layouts Compared: QWERTY, Dvorak, Colemak",
    }),
    link: {
      label: defineMessage({
        id: "t_Layouts",
        defaultMessage: "Layouts",
      }),
      title: defineMessage({
        id: "page.layouts.description",
        defaultMessage:
          "Compare over 90 keyboard layouts, including QWERTY, Dvorak and Colemak, by how much of your typing stays on the home row and how often each key is used.",
      }),
      icon: mdiKeyboardOutline,
    },
    meta,
  } satisfies PageInfo;

  export const design = {
    path: "/design",
    title: defineMessage({
      id: "t_Design",
      defaultMessage: "Design",
    }),
    link: {
      label: defineMessage({
        id: "t_Design",
        defaultMessage: "Design",
      }),
      title: defineMessage({
        id: "page.design.description",
        defaultMessage:
          "The TypeBlind design system — every screen in the dark and light themes.",
      }),
      icon: mdiPaletteOutline,
    },
    meta: [{ name: "robots", content: "noindex" }],
  } satisfies PageInfo;

  export const termsOfService = {
    path: "/terms-of-service",
    title: defineMessage({
      id: "t_Terms_of_Service",
      defaultMessage: "Terms of Service",
    }),
    link: {
      label: defineMessage({
        id: "t_Terms_of_Service",
        defaultMessage: "Terms of Service",
      }),
    },
    meta: [{ name: "robots", content: "noindex" }],
  } satisfies PageInfo;

  export const privacyPolicy = {
    path: "/privacy-policy",
    title: defineMessage({
      id: "t_Privacy_Policy",
      defaultMessage: "Privacy Policy",
    }),
    link: {
      label: defineMessage({
        id: "t_Privacy_Policy",
        defaultMessage: "Privacy Policy",
      }),
    },
    meta: [{ name: "robots", content: "noindex" }],
  } satisfies PageInfo;

  export function profileOf(arg: string): string;
  export function profileOf(arg: NamedUser): string;
  export function profileOf(arg: AnonymousUser): null;
  export function profileOf(arg: AnyUser): string | null;
  export function profileOf(arg: null): null;
  export function profileOf(arg: any): string | null {
    if (arg == null) {
      return null;
    }
    if (typeof arg === "string") {
      return `${Pages.profile.path}/${arg}`;
    }
    if (typeof arg === "object" && typeof arg.id === "string") {
      return `${Pages.profile.path}/${arg.id}`;
    }
    return null;
  }

  /**
   * The locales a page is offered to search engines in: its sitemap entries,
   * its hreflang alternates, and the locales whose address is its own
   * canonical. Every other locale prefix still works for visitors but
   * canonicalises to the English page.
   *
   * The landing page is written out in `landingLocales`. The other pages send
   * crawlers only an outline of a dozen words until their real components are
   * server-rendered, and offering 39 copies of each put 234 near-empty pages
   * into the sitemap - 95% of it - which is what Google files as Soft 404 and
   * crawls the site less for. Add a locale here once a page has real
   * server-rendered text in it.
   */
  export function indexedLocales(page: PageInfo): readonly LocaleId[] {
    return page.path === landing.path ? landingLocales : [defaultLocale];
  }

  export function intlBase(locale: string): string {
    return locale === defaultLocale ? "" : `/${locale}`;
  }

  export function intlPath(path: string, locale: string): string {
    return locale === defaultLocale
      ? path
      : path === "/"
        ? `/${locale}`
        : `/${locale}${path}`;
  }
}
